import classNames from "classnames";
import * as React from "react";
import { CSSProperties, useImperativeHandle, useRef } from "react";
import { Keys } from "~/common/enums";
import { commonClientResx } from "../../../resxDist/commonClientResx";
import { usePrevious } from "../../hooks/usePrevious";
import { useUpdateEffect } from "../../hooks/useUpdateEffect";
import { Input, inputTheme, ThemedInputProps } from "../../inputs";
import { AutoSizeInput } from "../../inputs/AutoSizeInput";
import * as styles from "./MultiSelect.less";

type Props = Pick<ThemedInputProps<string>, "style"> & {
    inputMaxLength?: number;
    placeholder?: string;
    className?: string;
    style?: CSSProperties;

    searchText: string;
    setSearchText: F1<string>;

    onKeyDown: F1<React.KeyboardEvent>;
    inputRef?: F1<Input>;

    canCreate?: boolean;

    selectedItems: any[];
    onUnselectLastItem: (e: React.KeyboardEvent) => void;

    children?: React.ReactNode;
};

export type MultiSelectInputRef = {
    focus: F;
};

export const MultiSelectInput = React.forwardRef<MultiSelectInputRef, Props>((props, ref) => {
    const {
        canCreate,
        inputMaxLength,
        searchText,
        setSearchText,
        onKeyDown,
        inputRef,
        placeholder = canCreate ? commonClientResx.Type_for_search_or_add : commonClientResx.Type_for_search,
        selectedItems,
        children,
        className,
        style,
        onUnselectLastItem,
    } = props;

    const innerInputRef = React.useRef<Input>();
    const setInputRef = (input: Input) => {
        innerInputRef.current = input;
        inputRef?.(input);
    };

    const onClick = () => {
        innerInputRef.current?.focusInput(false, { preventScroll: true });
    };

    const placeholderText = React.useMemo(() => {
        return (selectedItems.length < 1 && placeholder) || "";
    }, [selectedItems.length]);

    useResetSearchTextOnItemSelected(props);

    const wrapperClasses = classNames(styles.chips_wrapper, className);

    useImperativeHandle(ref, () => ({
        focus: () => innerInputRef.current?.focusInput(false),
    }));

    const onInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === Keys.Backspace && !searchText) {
            onUnselectLastItem(e);
            return;
        }

        onKeyDown(e);
    };

    const { containerRef } = useScrollToBottomOnItemSelection(selectedItems);

    return (
        <div className={wrapperClasses} ref={containerRef} onClick={onClick} data-testid={"multiselect-input"}>
            {children}
            <AutoSizeInput
                autoFocus={false}
                style={style}
                theme={inputTheme.naked}
                value={searchText}
                onChangeAction={setSearchText}
                onKeyDown={onInputKeyDown}
                placeholder={placeholderText}
                refCallback={setInputRef}
                maxLength={inputMaxLength}
                parentRef={containerRef}
            />
        </div>
    );
});

const useScrollToBottomOnItemSelection = (selectedItems: any[]) => {
    const containerRef = useRef<HTMLDivElement>();
    const prevSelectedItems = usePrevious(selectedItems);
    useUpdateEffect(() => {
        const isAdded = selectedItems.length > prevSelectedItems.length;
        if (isAdded && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [selectedItems]);

    return { containerRef };
};

export const useFocusMultiselectInput = <TInput extends MultiSelectInputRef>() => {
    const inputRef = useRef<TInput>();

    const focusInput = () => {
        inputRef.current?.focus();
    };

    return { inputRef, focusInput } as const;
};

const useResetSearchTextOnItemSelected = ({ selectedItems, setSearchText }: Props) => {
    const prevSelectedItemsCount = usePrevious(selectedItems.length);
    const itemsCount = selectedItems.length;
    useUpdateEffect(() => {
        const isItemAdded = itemsCount > prevSelectedItemsCount;
        if (isItemAdded) {
            setSearchText("");
        }
    }, [itemsCount]);
};
